import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { YouTubeLayer } from "./YouTubeLayer";
import { BRANDS } from "@/data/brands";
import { Eyebrow, MaskLines, EASE } from "./Motion";
import { useSectionNav } from "./Header";

const SPANS = [
  "lg:col-span-5 lg:row-span-2",
  "lg:col-span-4 lg:row-span-2",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-3 lg:row-span-2",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-5",
];

const setSpot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
};

const HoverReel = ({ name, active }) => (
  <video
    data-testid={`brand-tile-reel-${name}`}
    src={`/assets/reels/${name}.mp4`}
    muted
    loop
    playsInline
    preload="none"
    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${active ? "opacity-100" : "opacity-0"}`}
  />
);

const useHoverReel = (reelData) => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const enter = () => {
    if (reelData?.youtube) return setActive(true);
    const v = ref.current?.querySelector("video");
    if (!reelData || !v) return;
    v.currentTime = 0;
    v.play().then(() => setActive(true)).catch(() => {});
  };
  const leave = () => {
    const v = ref.current?.querySelector("video");
    setActive(false);
    if (v) setTimeout(() => v.pause(), 700);
  };
  return { ref, active, enter, leave };
};

const Tile = ({ b, i }) => {
  const reel = useHoverReel(b.reel);
  const [ytFailed, setYtFailed] = useState(false);
  const onYtError = useCallback(() => setYtFailed(true), []);
  return (
  <motion.div
    className={`h-[480px] sm:h-[420px] lg:h-auto ${SPANS[i]}`}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.15 }}
  >
    <motion.div className="h-full" variants={{ hidden: { clipPath: "inset(12% 12% 12% 12%)", opacity: 0 }, show: { clipPath: "inset(0% 0% 0% 0%)", opacity: 1 } }} transition={{ duration: 1.3, ease: EASE, delay: (i % 4) * 0.08 }}>
      <Link ref={reel.ref} to={`/brands/${b.slug}`} data-testid={`brand-tile-${b.slug}`} data-cursor="view" onMouseMove={setSpot} onMouseEnter={reel.enter} onMouseLeave={reel.leave} className="spot group relative block h-full overflow-hidden bg-[#121418]">
        <img src={b.cover} alt={`${b.name} ${b.sub} campaign`} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top grayscale-[35%] transition-[transform,filter] duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06] group-hover:grayscale-0" />
        {b.reel?.youtube && !ytFailed && (
          <div className={`transition-opacity duration-700 ${reel.active ? "opacity-100 delay-500" : "opacity-0"}`}>
            <YouTubeLayer id={b.reel.youtube} vertical={!!b.reel.vertical} playing={reel.active} onError={onYtError} testid={`brand-tile-reel-${b.slug}`} />
          </div>
        )}
        {b.reel?.name && <HoverReel name={b.reel.name} active={reel.active} />}
        <div className="absolute inset-0 z-[1] bg-[linear-gradient(to_bottom,rgba(10,11,13,0.55),transparent_22%,transparent_50%,rgba(10,11,13,0.92))]" />
        <div className="absolute left-5 right-5 top-5 z-[3] flex justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-[#f5f3ef]">
          <span className="rounded-full bg-[#0a0b0d]/55 px-3 py-1 backdrop-blur-md">{String(i + 1).padStart(2, "0")}</span>
          <span className="rounded-full bg-[#0a0b0d]/55 px-3 py-1 text-[#e4d3b8] backdrop-blur-md">{b.category}</span>
        </div>
        <div className="absolute inset-x-5 bottom-5 z-[3] flex items-end justify-between gap-4">
          <div>
            <div className="font-display text-4xl leading-none text-[#f5f3ef] sm:text-5xl">{b.name}</div>
            <div className="mt-2 text-sm italic text-[#d6d3cd] font-display">{b.sub}</div>
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/30 text-[#f5f3ef] transition-[background-color,color,transform] duration-500 group-hover:rotate-45 group-hover:border-[#c8a97e] group-hover:bg-[#c8a97e] group-hover:text-[#0a0b0d]">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </Link>
    </motion.div>
  </motion.div>
  );
};

const CtaTile = () => {
  const go = useSectionNav();
  return (
    <button data-testid="bento-book-tile" onClick={() => go("appointment")} className="group relative flex h-[300px] flex-col justify-between overflow-hidden border border-[#c8a97e]/40 bg-[#c8a97e] p-6 text-left text-[#0a0b0d] lg:col-span-4 lg:h-auto">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Dundrum Showroom</span>
      <span className="font-display text-4xl leading-[1] sm:text-5xl">See the new season<br /><em>in person.</em></span>
      <span className="flex items-center gap-2 text-[13px] font-medium uppercase tracking-[0.16em]">Book an appointment <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" /></span>
    </button>
  );
};

export const BrandBento = () => (
  <section id="brands" data-testid="brands-section" className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10 sm:py-32 lg:px-14">
    <div className="mb-16 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end">
      <div className="lg:col-span-8">
        <Eyebrow index="02">The House Edit</Eyebrow>
        <h2 className="mt-8 font-display text-6xl leading-[0.9] sm:text-7xl lg:text-8xl">
          <MaskLines inView lines={["Nine houses.", <em key="o" className="text-[#c8a97e]">One address.</em>]} />
        </h2>
      </div>
      <p className="max-w-sm text-base leading-relaxed text-[#c9c6c0] lg:col-span-4">
        A strategic multi-channel platform for global and international fashion and accessories brands — wholesale, franchise and retail concepts across Ireland.
      </p>
    </div>
    <div className="grid grid-flow-dense grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[300px] lg:gap-5">
      {BRANDS.map((b, i) => <Tile key={b.slug} b={b} i={i} />)}
      <CtaTile />
    </div>
  </section>
);
